import React from 'react';
import { Box } from '@mui/material';
import {
  CheckCircle as HealthyIcon,
  Warning as WarningIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';

// Traffic-light thresholds: green < amber < red. Same shape works for
// concurrency-vs-limit (percent), throttle counts (absolute), error rates.
// Extracted from AwsHealthPage so the main Dashboard tile can render the
// same Lambda concurrency / DLQ depth tiles without duplicating helpers.
export type Severity = 'healthy' | 'warning' | 'critical';

export function severityFromPercent(pct: number): Severity {
  if (pct >= 80) return 'critical';
  if (pct >= 50) return 'warning';
  return 'healthy';
}

export function severityFromAbsolute(value: number, warnAt: number, critAt: number): Severity {
  if (value >= critAt) return 'critical';
  if (value >= warnAt) return 'warning';
  return 'healthy';
}

export function severityColor(s: Severity): string {
  if (s === 'critical') return '#e53e3e';
  if (s === 'warning') return '#dd6b20';
  return '#38a169';
}

export function severityIcon(s: Severity): React.ReactElement {
  if (s === 'critical') return <ErrorIcon sx={{ color: severityColor(s) }} />;
  if (s === 'warning') return <WarningIcon sx={{ color: severityColor(s) }} />;
  return <HealthyIcon sx={{ color: severityColor(s) }} />;
}

// Lightweight inline-SVG sparkline. Avoids pulling Recharts in just to draw
// a few dots — Recharts' ResponsiveContainer doesn't render in jsdom and
// complicates tests for no functional gain.
export const Sparkline: React.FC<{
  values: number[];
  color: string;
  width?: number;
  height?: number;
}> = ({ values, color, width = 240, height = 64 }) => {
  if (values.length < 2) return null;
  const max = Math.max(...values, 1);
  const stepX = width / (values.length - 1);
  const path = values
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * stepX).toFixed(2)},${(height - (v / max) * height).toFixed(2)}`)
    .join(' ');
  return (
    <Box sx={{ mt: 2 }}>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <path d={path} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </Box>
  );
};
